<!--
  KEY Inspector — DEV-ONLY debug button + modal for a single market.

  Renders a small icon button next to a market when BOTH:
    - window.__DEBUG_KEY_UI === true         (opt-in via DevTools)
    - market.keyApplied != null              (there's a KEY worth inspecting)

  Clicking the button opens a modal that shows:
    - Current state: keyApplied, providerBook, multiplier, keyFallback
    - Per-outcome breakdown: raw / KEY-adjusted / active / blockedByKey
    - What-if simulator: type a hypothetical key, see the rescaled odds
      that WOULD result (pure computation — store is NEVER mutated)

  This component is self-contained. Import it in a market-renderer and drop
  <KeyInspector :market="market" /> next to the market header. In prod the
  debug flag is off so the button is never rendered (v-if excludes it from
  the DOM completely — no cost).
-->
<template>
  <span v-if="visible" class="key-inspector">
    <v-btn icon x-small :color="hasKey ? 'deep-purple' : 'grey'"
           class="key-inspector-btn"
           :class="{'key-inspector-btn--nokey': !hasKey}"
           @click.stop="dialog = true"
           :title="hasKey ? 'KEY inspector — key applied' : 'KEY inspector — no key (simulator only)'">
      <v-icon x-small>{{ hasKey ? 'mdi-flask' : 'mdi-flask-outline' }}</v-icon>
    </v-btn>

    <v-dialog v-model="dialog" :max-width="640" scrollable>
      <v-card v-if="market" class="key-inspector-card">
        <v-toolbar dense flat color="deep-purple" dark>
          <v-icon left>mdi-flask-outline</v-icon>
          <v-toolbar-title class="text-subtitle-2 font-weight-bold">
            KEY Inspector — {{ market.marketName || market.name || market.id }}
            <v-chip v-if="specifierLabel" x-small class="ml-2 font-weight-bold"
                    color="deep-purple lighten-3" text-color="deep-purple darken-4">
              <v-icon x-small left>mdi-tune-variant</v-icon>
              {{ specifierLabel }}
            </v-chip>
            <v-chip v-else-if="hasMixedSpecifiers" x-small class="ml-2 font-weight-bold"
                    color="amber lighten-3" text-color="amber darken-4"
                    title="This modal spans multiple specifier lines — see per-outcome column">
              <v-icon x-small left>mdi-tune-variant</v-icon>
              multi-line
            </v-chip>
          </v-toolbar-title>
          <v-spacer/>
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <!-- CURRENT STATE ------------------------------------------------ -->
          <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-1 mb-1">
            <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
            Current state
          </div>
          <v-simple-table dense class="mb-4">
            <template v-slot:default>
              <tbody>
                <tr>
                  <td class="text-caption grey--text">marketId</td>
                  <td class="mono">{{ market.id || market.unique_id }}</td>
                </tr>
                <tr>
                  <td class="text-caption grey--text">specifier</td>
                  <td>
                    <span v-if="specifierLabel" class="mono font-weight-bold">
                      {{ specifierLabel }}
                    </span>
                    <span v-else-if="hasMixedSpecifiers" class="text-caption amber--text text--darken-3">
                      <v-icon x-small color="amber darken-3">mdi-alert-circle-outline</v-icon>
                      multiple lines in this market — see per-outcome column
                    </span>
                    <span v-else class="grey--text">—</span>
                  </td>
                </tr>
                <tr>
                  <td class="text-caption grey--text">keyApplied (server)</td>
                  <td>
                    <v-chip small :color="market.keyApplied != null ? 'success' : 'grey'" dark>
                      {{ market.keyApplied != null ? market.keyApplied : 'none' }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="text-caption grey--text">providerBook (from raw)</td>
                  <td class="mono">{{ providerBook != null ? providerBook.toFixed(2) : '—' }}</td>
                </tr>
                <tr>
                  <td class="text-caption grey--text">multiplier</td>
                  <td class="mono">{{ currentMultiplier != null ? currentMultiplier.toFixed(4) : '—' }}</td>
                </tr>
                <tr v-if="market.keyFallback">
                  <td class="text-caption grey--text">keyFallback</td>
                  <td>
                    <v-chip small color="warning" dark>
                      {{ typeof market.keyFallback === 'string' ? market.keyFallback : market.keyFallback.reason }}
                    </v-chip>
                  </td>
                </tr>
                <tr v-if="providerClosedCount > 0">
                  <td class="text-caption grey--text">provider status</td>
                  <td class="text-caption">
                    <v-chip small color="amber darken-2" dark>
                      {{ (market.outcome || []).length - providerClosedCount }} open / {{ providerClosedCount }} closed
                    </v-chip>
                    <span class="ml-2 grey--text">
                      book calcolato su tutti gli outcome (shadow-odds)
                    </span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- HINT: shadow-odds book explanation -------------------------- -->
          <v-alert v-if="providerClosedCount > 0" dense outlined type="info"
                   class="text-caption mb-4" icon="mdi-information-outline">
            <strong>Shadow-odds book</strong> — {{ providerClosedCount }} outcome
            {{ providerClosedCount === 1 ? 'è' : 'sono' }} provider-closed
            (<code>active=false</code>). La loro quota raw è comunque inclusa
            nel calcolo del <code>providerBook</code> come "quota ombra" —
            così il multiplier resta coerente col book naturale del provider
            (es. Doppia Chance ~208%, non ~19% se filtrassimo). Le quote
            degli outcome closed vengono rescalate per display, ma il loro
            status resta <code>closed</code> (mai riaperto). Il floor 1.01
            applica per outcome, granulare — non chiude tutto il market.
            Vedi <code>K.11</code> nel KEY README per la math completa.
          </v-alert>

          <!-- OUTCOMES ---------------------------------------------------- -->
          <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-1 mb-1">
            <v-icon x-small class="mr-1">mdi-format-list-bulleted</v-icon>
            Outcomes
          </div>
          <v-simple-table dense class="mb-4">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-caption">Name</th>
                  <th v-if="hasMixedSpecifiers" class="text-caption">spec</th>
                  <th class="text-caption text-right">raw</th>
                  <th class="text-caption text-right">applied</th>
                  <th class="text-caption text-center">active</th>
                  <th class="text-caption text-center">blockedByKey</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="o in market.outcome || []" :key="o.unique_id || o.outcome_id">
                  <td class="text-caption">{{ o.outcome_alias || o.name || o.outcomeName || o.unique_id }}</td>
                  <td v-if="hasMixedSpecifiers" class="text-caption mono">
                    <v-chip x-small outlined color="deep-purple">
                      {{ _outcomeSpecifierLabel(o) || '—' }}
                    </v-chip>
                  </td>
                  <td class="text-caption text-right mono">{{ o.outcome_odd_original }}</td>
                  <td class="text-caption text-right mono">{{ o.outcome_odd_value }}</td>
                  <td class="text-center">
                    <v-icon x-small :color="o.active ? 'success' : 'grey'">
                      {{ o.active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                  </td>
                  <td class="text-center">
                    <v-icon v-if="o.blockedByKey" x-small color="red">mdi-lock</v-icon>
                    <span v-else class="grey--text">—</span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- WHAT-IF SIMULATOR ------------------------------------------- -->
          <v-divider class="my-3"/>
          <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-1 mb-1">
            <v-icon x-small class="mr-1">mdi-calculator</v-icon>
            What-if simulator
          </div>
          <p class="text-caption grey--text mb-2">
            Type a hypothetical KEY to see the rescaled outcomes. The store
            is NOT mutated — this is a pure preview.
          </p>
          <v-row dense align="center">
            <v-col cols="6" sm="4">
              <v-text-field v-model.number="hypotheticalKey"
                            type="number" step="1" min="0"
                            label="Hypothetical KEY"
                            outlined dense hide-details
                            prepend-inner-icon="mdi-key-variant"/>
            </v-col>
            <v-col cols="6" sm="8">
              <div v-if="simulation" class="text-caption">
                <span class="mono">multiplier {{ simulation.multiplier }}</span>
                <span v-if="simulation.fallbackReason" class="ml-2 red--text">
                  <v-icon x-small color="red">mdi-alert</v-icon>
                  fallback: {{ simulation.fallbackReason }}
                </span>
              </div>
            </v-col>
          </v-row>

          <v-simple-table v-if="simulation" dense class="mt-2">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-caption">Name</th>
                  <th class="text-caption text-right">raw</th>
                  <th class="text-caption text-right">simulated</th>
                  <th class="text-caption text-center">blocked?</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in simulation.outcomes" :key="s.id"
                    :class="{'red lighten-5': s.wouldBeBlocked}">
                  <td class="text-caption">{{ s.name || s.id }}</td>
                  <td class="text-caption text-right mono">{{ s.raw }}</td>
                  <td class="text-caption text-right mono">
                    <span :class="{'red--text font-weight-bold': s.wouldBeBlocked}">
                      {{ s.simulated }}
                    </span>
                  </td>
                  <td class="text-center">
                    <v-icon v-if="s.wouldBeBlocked" x-small color="red">mdi-lock</v-icon>
                    <v-icon v-else x-small color="success">mdi-check</v-icon>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import { simulateKeyOnMarket } from '@/services/keyLiveReapply.service';

export default {
    name: 'KeyInspector',
    props: {
        market: { type: Object, required: true }
    },
    data() {
        return {
            dialog: false,
            hypotheticalKey: null,
            // The debug flag is a plain global on `window`. Vue doesn't track
            // it reactively — we hook into it via a computed that reads the
            // ref refreshed at every render pass (via `debugTick`).
            debugTick: 0
        };
    },
    computed: {
        visible() {
            // Referenced to trigger reactivity through debugTick when
            // toggled from DevTools. In production the flag is undefined /
            // false → the whole button + modal is v-if excluded.
            void this.debugTick;
            const flag = typeof window !== 'undefined' && window.__DEBUG_KEY_UI === true;
            return flag
                && this.market
                && Array.isArray(this.market.outcome)
                && this.market.outcome.length > 0;
        },
        hasKey() {
            return this.market && this.market.keyApplied != null;
        },
        // Best-effort specifier resolution for the modal title / current-state
        // row. Sources checked, in order:
        //   1. `market.specifier`         — parent renderer already scoped
        //                                    the market card per line.
        //   2. `market.specifiers`        — Over/Under-style flat string.
        //   3. Outcome-level specifiers   — if EVERY outcome shares the
        //                                    same specifier, use it.
        // Returns null when the market spans multiple specifier lines (in
        // which case `hasMixedSpecifiers` is true and we render a per-
        // outcome column instead).
        specifierLabel() {
            if (!this.market) return null;
            const direct = this.market.specifier ?? this.market.specifiers;
            if (direct != null && direct !== '') return String(direct);
            const uniq = this._distinctOutcomeSpecifiers;
            if (uniq.length === 1 && uniq[0] !== '') return uniq[0];
            return null;
        },
        hasMixedSpecifiers() {
            // >1 distinct non-empty specifier across outcomes AND no single
            // canonical value on the market itself. The `!specifierLabel`
            // guard prevents an amber chip when the market IS scoped but
            // outcomes happen to carry redundant identical specifiers.
            if (this.specifierLabel) return false;
            return this._distinctOutcomeSpecifiers.length > 1;
        },
        _distinctOutcomeSpecifiers() {
            const outcomes = this.market?.outcome || [];
            const seen = new Set();
            for (const o of outcomes) {
                const s = o.specifiers ?? o.specifier;
                if (s == null || s === '') continue;
                seen.add(String(s));
            }
            return Array.from(seen);
        },
        providerBook() {
            return this._bookStats.sum;
        },
        // Legacy computed names kept because the template still references
        // them in a few spots — semantics adjusted for K.11.
        participatingCount() {
            return this._bookStats.included;
        },
        excludedCount() {
            return this._bookStats.excluded;
        },
        providerClosedCount() {
            return this._bookStats.providerClosed;
        },
        _bookStats() {
            // K.11 — SHADOW-ODDS BOOK. Match the server-side resolver:
            // every outcome with a valid raw contributes to the book,
            // regardless of active state. `excluded` here only counts
            // outcomes with a missing/invalid raw (i.e. bad payload
            // fixtures), NOT provider-closed ones — those still show
            // up in the book as shadow odds. `providerClosed` gives
            // the info the "partial book" hint used to encode, without
            // implying the book is inaccurate.
            const outcomes = this.market?.outcome || [];
            let sum = 0;
            let included = 0;
            let excluded = 0;
            let providerClosed = 0;
            for (const o of outcomes) {
                const raw = parseFloat(o.outcome_odd_original);
                if (!Number.isFinite(raw) || raw <= 0) { excluded++; continue; }
                sum += 100 / raw;
                included++;
                if (o.active !== true) providerClosed++;
            }
            return {
                sum: included > 0 ? sum : null,
                included,
                excluded,
                providerClosed
            };
        },
        currentMultiplier() {
            if (this.providerBook == null || !this.market?.keyApplied) return null;
            return this.providerBook / Number(this.market.keyApplied);
        },
        simulation() {
            if (this.hypotheticalKey == null || this.hypotheticalKey === '') return null;
            return simulateKeyOnMarket(this.market, this.hypotheticalKey);
        }
    },
    methods: {
        _outcomeSpecifierLabel(outcome) {
            const s = outcome?.specifiers ?? outcome?.specifier;
            return s == null || s === '' ? null : String(s);
        }
    },
    mounted() {
        // Poll the global flag every second in case someone flips it in
        // DevTools with the modal already mounted. Cheap — no work when the
        // flag doesn't change.
        this._debugPollId = setInterval(() => { this.debugTick++; }, 1000);
        // Seed the simulator with the currently applied key so the modal
        // opens with a meaningful reference point.
        if (this.market?.keyApplied != null && this.hypotheticalKey == null) {
            this.hypotheticalKey = Number(this.market.keyApplied);
        }
    },
    beforeDestroy() {
        if (this._debugPollId) clearInterval(this._debugPollId);
    }
};
</script>

<style scoped>
.key-inspector { display: inline-block; }
.key-inspector-btn { vertical-align: middle; }
.key-inspector-btn--nokey { opacity: 0.45; }
.key-inspector-btn--nokey:hover { opacity: 1; }
.key-inspector-card .mono {
    font-family: 'Roboto Mono', 'Consolas', monospace;
    font-size: var(--font-size-3xl);
}
</style>
